// ============================================================
// Die rechnenden Teile von „Aktuelles" (§16) — ohne React, ohne Netz.
//
// Alles hier ist eine reine Funktion: dieselbe Eingabe, dieselbe Ausgabe. Das
// ist kein Selbstzweck, sondern der Grund, warum `hilfen.test.ts` diese Datei
// prüfen kann, ohne einen Bildschirm zu bauen — und der Bildschirm daneben
// enthält dann nur noch Anzeige.
//
// Zwei Zahlen werden hier BEWUSST im Client gerechnet und nicht vom Server
// geholt (SPEC §16.2 lässt beides offen und entscheidet sich für den Client):
// die Bereichszähler und „Erfolge dieser Woche". Beide beziehen sich auf die
// bereits GELADENEN Seiten und sind damit ehrlich gesagt eine Aussage über
// das, was man sieht — eine zweite Route für eine Zahl neben einem Wort wäre
// ein Preis ohne Gegenwert.
// ============================================================
import type { AktuellesSeite, Bereich, BereichEintrag, BeitragKurz, Sprache } from "../../api/types";

/* ------------------------------------------------------------ Grenzen (§16.1) */

export const MAX_TITEL = 160;
export const MAX_TEXT = 4000;
export const MAX_KOMMENTAR = 2000;
export const MAX_NACHWEIS = 200;

/** Dieselbe Grenze wie in der Projektgalerie (`medien.py`, SPEC §6). */
export const MAX_DATEI_BYTES = 40 * 1024 * 1024;
export const DATEI_ACCEPT = "image/jpeg,image/png,image/webp,image/tiff,image/heic,image/heif,application/pdf";

/* ------------------------------------------------------- Seiten und Zeiger */

/**
 * Die geladenen Seiten zu EINER Liste zusammenlegen.
 *
 * Doppelte fallen heraus, und zwar der SPÄTERE: zwischen zwei Abrufen kann ein
 * Beitrag kommentiert worden sein und damit seine Stelle wechseln — dann steht
 * er auf Seite 1 und noch einmal auf Seite 2. Ohne diese Zeile zeichnete React
 * zwei Karten mit demselben Schlüssel, und genau das ist der Fehler, der sich
 * als „eine Karte lässt sich nicht mehr anklicken" meldet.
 */
export function seitenZusammen(seiten: readonly AktuellesSeite[] | undefined): BeitragKurz[] {
  if (!seiten) return [];
  const gesehen = new Set<string>();
  const raus: BeitragKurz[] = [];
  for (const s of seiten) {
    for (const b of s.beitraege) {
      if (gesehen.has(b.id)) continue;
      gesehen.add(b.id);
      raus.push(b);
    }
  }
  return raus;
}

/** Der Zeiger der letzten Seite — `null` heißt „das war alles". */
export function naechsterZeiger(seiten: readonly AktuellesSeite[] | undefined): string | null {
  if (!seiten || seiten.length === 0) return null;
  return seiten[seiten.length - 1]?.weiter ?? null;
}

/* ---------------------------------------------------------------- Bereiche */

/**
 * Der Anzeigename eines Bereichs in der Sprache der Oberfläche (§16.6).
 *
 * Zwei Rückfälle, beide absichtlich:
 *
 *  · Fehlt die Fassung in der gewählten Sprache (`name_en` ist freiwillig), gilt
 *    die andere — dieselbe Regel wie bei den Phasen (`lib/texte.ts: phasenName`).
 *  · Ist der Bereich (noch) nicht geladen oder inzwischen gelöscht, bleibt der
 *    SCHLÜSSEL stehen. Ein leeres Feld wäre die schlechtere Wahl: der Schlüssel
 *    ist lesbar genug für eine Plakette und sagt, wonach zu suchen ist.
 */
export function bereichName(bereiche: readonly BereichEintrag[], schluessel: Bereich, sprache: Sprache): string {
  const b = bereiche.find((x) => x.schluessel === schluessel);
  if (!b) return schluessel;
  return sprache === "en" ? b.name_en || b.name_de : b.name_de || b.name_en;
}

/**
 * Welche Bereiche stehen zur Auswahl?
 *
 * Die aktiven — UND der gerade gewählte, auch wenn er inzwischen stillgelegt
 * wurde. Ohne diese Ausnahme verschwände die Liste unter der eigenen Auswahl:
 * die linke Spalte zeigte dann einen Filter, den man nicht mehr sieht, und der
 * Feed bliebe auf einer Menge stehen, für die es keine Schaltfläche gibt.
 */
export function waehlbareBereiche(bereiche: readonly BereichEintrag[], gewaehlt: Bereich | ""): BereichEintrag[] {
  return bereiche.filter((b) => b.aktiv || b.schluessel === gewaehlt);
}

/* ----------------------------------------------------------- Bereichszähler */

export interface BereichZahl {
  bereich: Bereich | null;
  zahl: number;
}

/**
 * Wie viele der geladenen Beiträge je Bereich?
 *
 * Der erste Eintrag ist IMMER „alles" (`bereich: null`) mit der Gesamtzahl —
 * so hat die linke Spalte eine Zeile je Schaltfläche und die Oberfläche muss
 * den Sonderfall nicht selbst davorsetzen. Beiträge OHNE Bereich (eine News
 * darf keinen haben) zählen in „alles" mit und sonst nirgends; das ist die
 * Lesart der Entwurfsleinwand, auf der die Summe der Bereichszeilen kleiner
 * sein darf als die Zahl oben.
 *
 * Seit §16.6 kommt die Liste der Bereiche von außen (`GET /aktuelles/bereiche`)
 * statt aus einer Konstante. Welche darin stehen, entscheidet der AUFRUFER —
 * diese Funktion nimmt sie, wie sie kommen, und in dieser Reihenfolge. Das ist
 * Absicht: der Bildschirm blendet stillgelegte Bereiche aus, muss aber den
 * gerade gewählten stehen lassen, und diese Regel gehört an die eine Stelle, an
 * der die Auswahl bekannt ist.
 */
export function bereichZahlen(beitraege: readonly BeitragKurz[], bereiche: readonly BereichEintrag[]): BereichZahl[] {
  const zaehler = new Map<Bereich, number>();
  for (const b of beitraege) {
    if (!b.bereich) continue;
    zaehler.set(b.bereich, (zaehler.get(b.bereich) ?? 0) + 1);
  }
  return [
    { bereich: null, zahl: beitraege.length },
    ...bereiche.map((s) => ({ bereich: s.schluessel, zahl: zaehler.get(s.schluessel) ?? 0 })),
  ];
}

/* ------------------------------------------------------------- Offene Fragen */

/** Eine Frage gilt als offen, solange niemand eine Antwort markiert hat. Der Beitrag eines
 *  Rückblicks (§51.3, `rueckblick_id`) steht im Fragen-Faden, ist aber keine Frage — er wartet auf
 *  keine Antwort und zählt nie als offen: weder im Zähler „offen" des Reiters Fragen noch in den
 *  Projektfäden noch im Kopf von Aktuelles. `== null` an BEIDEN Feldern: JSON-null kann als
 *  undefined ankommen (§38.11) — mit `=== null` galt eine Frage ohne Antwort dann als beantwortet. */
export function istOffeneFrage(b: BeitragKurz): boolean {
  return b.art === "frage" && b.beantwortet_kommentar_id == null && b.rueckblick_id == null;
}

export function offeneFragen(beitraege: readonly BeitragKurz[]): BeitragKurz[] {
  return beitraege.filter(istOffeneFrage);
}

export interface Projektfaden {
  projekt_id: string;
  offen: number;
}

/**
 * Die Projektfäden der linken Spalte: MEINE Projekte, in denen eine Frage
 * offen ist — absteigend nach Zahl, bei Gleichstand in der Reihenfolge der
 * übergebenen Projektliste (die kommt sortiert aus `Stand`).
 *
 * `meineProjekte` ist eine Liste und kein Set, weil genau diese Reihenfolge
 * gebraucht wird; die Zugehörigkeit wird daraus einmal als Set gebaut.
 */
export function projektfaeden(beitraege: readonly BeitragKurz[], meineProjekte: readonly string[]): Projektfaden[] {
  const meine = new Set(meineProjekte);
  const zaehler = new Map<string, number>();
  for (const b of beitraege) {
    if (!b.projekt_id || !meine.has(b.projekt_id) || !istOffeneFrage(b)) continue;
    zaehler.set(b.projekt_id, (zaehler.get(b.projekt_id) ?? 0) + 1);
  }
  const rang = new Map(meineProjekte.map((id, i) => [id, i]));
  return [...zaehler.entries()]
    .map(([projekt_id, offen]) => ({ projekt_id, offen }))
    .sort((a, b) => b.offen - a.offen || (rang.get(a.projekt_id) ?? 0) - (rang.get(b.projekt_id) ?? 0));
}

/* ------------------------------------------------------ Erfolge dieser Woche */

/** Sieben Tage, in Millisekunden — die Spanne der rechten Spalte. */
export const WOCHE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Die Erfolge der letzten sieben Tage, neueste zuerst.
 *
 * Gerechnet wird über einen ZEITSTEMPEL und nicht über Kalenderwochen: „diese
 * Woche" heißt hier „seit sieben Tagen" — sonst stünde die Spalte montags
 * früh leer, obwohl am Freitag zuvor drei Abgaben rausgingen, und genau
 * freitags gehen sie raus.
 *
 * Ein unlesbarer Zeitstempel fällt heraus, statt die Liste zu sprengen.
 */
export function erfolgeDerWoche(beitraege: readonly BeitragKurz[], jetzt: Date = new Date()): BeitragKurz[] {
  const grenze = jetzt.getTime() - WOCHE_MS;
  return beitraege
    .filter((b) => {
      if (!b.erfolg_art) return false;
      const t = Date.parse(b.angelegt_am);
      return Number.isFinite(t) && t >= grenze && t <= jetzt.getTime() + 60_000;
    })
    .sort((a, b) => b.angelegt_am.localeCompare(a.angelegt_am));
}

/* ---------------------------------------------------------- Ungelesen-Punkt */

/**
 * Der Merker im Browserspeicher. Er steht HIER und nicht im Bildschirm: die
 * Kopfleiste liest ihn bei jedem Anstrich, und ein Import aus `Aktuelles.tsx`
 * zöge den ganzen, absichtlich nachgeladenen Bildschirm ins Hauptbündel.
 */
export const GESEHEN_SCHLUESSEL = "aktuelles.gesehen";

/**
 * Gibt es etwas Neues seit dem letzten Besuch?
 *
 * `gesehen` kommt aus dem Browserspeicher und nicht aus `Ich`: das Feld
 * `aktuelles_gesehen_am` steht zwar in der Datenbank (§16.1), aber NICHT im
 * Typvertrag — `POST /aktuelles/gesehen` setzt es serverseitig für den
 * Wochenbrief, und der Punkt in der Kopfleiste rechnet lokal. Ohne
 * gespeicherten Wert gibt es keinen Punkt: ein roter Punkt beim allerersten
 * Besuch wäre eine Behauptung über etwas, das man noch gar nicht verpasst
 * haben kann.
 */
export function hatUngelesen(neuestes: string | null | undefined, gesehen: string | null): boolean {
  if (!neuestes || !gesehen) return false;
  const a = Date.parse(neuestes);
  const b = Date.parse(gesehen);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return a > b;
}

/** Der jüngste Zeitstempel einer Seite — die Liste kommt neueste zuerst. */
export function neuestesDatum(beitraege: readonly BeitragKurz[]): string | null {
  let jung: string | null = null;
  for (const b of beitraege) {
    if (jung === null || b.angelegt_am.localeCompare(jung) > 0) jung = b.angelegt_am;
  }
  return jung;
}

/* ------------------------------------------------------------------ Zitate */

/**
 * Die Vorsilbe, die „Zitieren" in das Antwortfeld schreibt. Lange Zitate
 * werden gekappt: ein Zitat ist ein Anker, keine zweite Kopie des Beitrags.
 */
export function zitatKuerzen(text: string, grenze = 140): string {
  const eine = text.replace(/\s+/g, " ").trim();
  if (eine.length <= grenze) return eine;
  return eine.slice(0, grenze - 1).trimEnd() + "…";
}
